import type { TFunction } from 'i18next';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../api/projects-client/projects-client.type';
import type { Goal, Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { formatCurrency } from '../../../../helpers/currency-helpers';
import { formatInitiativeDate } from '../../strategy.helpers';
import { getContributingTeams } from '../main-section/main-section.helpers';

type EntityType = 'initiative' | 'outcome' | 'goal';
type StrategyEntity = Initiative | Outcome | Goal;

// Type guards
function isInitiative(entity: StrategyEntity): entity is Initiative {
  return 'portfolio' in entity;
}

/**
 * Generates an array of label-value pairs containing entity information
 *
 * @param entity - The entity object containing the data (initiative, outcome, or goal)
 * @param entityType - The type of entity ('initiative', 'outcome', or 'goal')
 * @param teams - The list of teams
 * @param t - Translation function from `useTranslation()`
 * @param currencyCode - Currency code for cost formatting (default 'USD')
 * @returns An array of objects with label and value properties representing entity details
 */
const getEntityInfo = (
  entity: StrategyEntity,
  _entityType: EntityType,
  teams: Team[],
  t: TFunction,
  currencyCode = 'USD',
): { label: string; value: string | number | null | undefined }[] => {
  if (!entity) {
    return [];
  }

  const baseInfo = [
    {
      label: t('strategy.initiative_performance.start_date'),
      value: formatInitiativeDate(entity.start_date, t),
    },
    {
      label: t('strategy.initiative_performance.end_date'),
      value: formatInitiativeDate(entity.end_date, t),
    },
  ];

  // Add activity dates for outcomes and goals
  if ('activity_start_date' in entity) {
    baseInfo.push({
      label: t('strategy.initiative_performance.activity_start_date'),
      value:
        entity.activity_start_date && entity.activity_start_date !== entity.start_date
          ? formatInitiativeDate(entity.activity_start_date, t)
          : t('strategy.initiatives.labels.same_as_start_date', 'Same as Start Date'),
    });
    baseInfo.push({
      label: t('strategy.initiative_performance.activity_end_date'),
      value:
        entity.activity_end_date && entity.activity_end_date !== entity.end_date
          ? formatInitiativeDate(entity.activity_end_date, t)
          : t('strategy.initiatives.labels.same_as_end_date', 'Same as End Date'),
    });
  } else if (isInitiative(entity)) {
    // Add effective dates for initiatives
    baseInfo.push({
      label: t('strategy.initiative_performance.activity_start_date'),
      value:
        entity.effective_start_date && entity.effective_start_date !== entity.start_date
          ? formatInitiativeDate(entity.effective_start_date, t)
          : t('strategy.initiatives.labels.same_as_start_date', 'Same as Start Date'),
    });
    baseInfo.push({
      label: t('strategy.initiative_performance.activity_end_date'),
      value:
        entity.effective_end_date && entity.effective_end_date !== entity.end_date
          ? formatInitiativeDate(entity.effective_end_date, t)
          : t('strategy.initiatives.labels.same_as_end_date', 'Same as End Date'),
    });
  }

  // Add status information (all entity types have this)
  baseInfo.push({
    label: t('strategy.initiatives.labels.status_by_points', { estimation: t('common.estimation') }),
    value: getEntityStatus(entity.status?.by_points?.status, t) || t('strategy.initiatives.labels.no_data', '-'),
  });
  baseInfo.push({
    label: t('strategy.initiatives.labels.status_by_tasks', { work_units: t('common.work_units') }),
    value: getEntityStatus(entity.status?.by_tasks?.status, t) || t('strategy.initiatives.labels.no_data', '-'),
  });

  // Add initiative-specific fields
  if (isInitiative(entity)) {
    baseInfo.push({
      label: t('strategy.initiative_performance.cost_to_date'),
      value: formatCurrency(entity.costs_to_date?.initiative || 0, { currency: currencyCode }),
    });
    baseInfo.push({
      label: t('common.teams'),
      value: getContributingTeams(entity, teams) || t('strategy.initiatives.labels.no_data', '-'),
    });
  }

  // Add business value (all types have this)
  if (entity.relative_business_value !== null && entity.relative_business_value !== undefined) {
    baseInfo.push({
      label: t('strategy.initiatives.form.business_value_label'),
      value:
        entity.relative_business_value !== null && entity.relative_business_value !== undefined
          ? String(entity.relative_business_value)
          : t('strategy.initiatives.labels.no_data', '-'),
    });
  }

  return baseInfo;
};

/**
 * Helper method to get the string of the entity status
 *
 * @param {string | undefined} status - status string ('on_track', 'at_risk', 'off_track', 'unknown')
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} the label string of the status
 */
const getEntityStatus = (status: string | undefined, t: TFunction): string => {
  const labelMapping: Record<string, string> = {
    at_risk: t('strategy.initiatives.statuses.at_risk', 'At Risk'),
    off_track: t('strategy.initiatives.statuses.off_track', 'Off Track'),
    on_track: t('strategy.initiatives.statuses.on_track', 'On Track'),
    unknown: t('strategy.initiatives.statuses.unknown', 'Unknown'),
    empty: t('strategy.initiatives.statuses.empty', 'Empty'),
  };

  return status
    ? labelMapping[status] || t('strategy.initiatives.statuses.unknown', 'Unknown')
    : t('strategy.initiatives.statuses.unknown', 'Unknown');
};

export { getEntityInfo, getEntityStatus };
